import React from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
	Divider,
	Card,
	Fade,
	Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import BrushIcon from "@material-ui/icons/Brush";
import ImageIcon from "@material-ui/icons/Image";
import SaveIcon from "@material-ui/icons/Save";
import UpdateIcon from "@material-ui/icons/Update";
import LayersIcon from "@material-ui/icons/Layers";
import EditIcon from "@material-ui/icons/Edit";
import _ from "lodash";
import EditorTool from "../../../components/EditorTool";
import ItemSelected from "./components/ItemSelected";
import useSelectionPage from "./hook/useSelectionPages";
import useStyles from "./styled";

const fields = [
	{field: "Product SKU", options: ["IN", "NOT IN"]},
	{field: "Product ID", options: ["IN", "NOT IN"]},
	{field: "Category", options: ["IN", "NOT IN"]},
	{field: "Subcategory", options: ["IN", "NOT IN"]},
	{field: "Product Name", options: ["starts with", "contains"]},
	{field: "Product Description", options: ["starts with", "contains"]},
	{field: "Price", options: ["bigger than", "less than"]},
	{field: "Supplier", options: ["IN", "NOT IN"]},
	{field: "Brand", options: ["IN", "NOT IN"]},
	{field: "Country", options: ["IN", "NOT IN"]},
	{field: "Dietary", options: ["IN", "NOT IN"]},
	{field: "Discount", options: ["YES", "NO"]},
	{field: "New Arrival", options: ["YES", "NO"]},
	{field: "Trending", options: ["YES", "NO"]},
];

const selectionPageEntityType = [
	{key: "CATEGORY", value: "Category", id: 1},
	{key: "SUBCATEGORY", value: "SubCategory", id: 2}
];

const landingPagesType = [
	{key: "CATEGORY", value: "Category"},
	{key: "SUBCATEGORY", value: "SubCategory"},
	{key: "RECIPE", value: "Recipe"},
	{key: "MARKETING", value: "Marketing"},
	{key: "SEASONAL", value: "Seasonal"},
];

const ComplexSelectionCriteria = () => {
	const classes = useStyles();
	const {
		blocks,
		formData,
		loading,
		type,
		pageType,
		updateFormField,
		generateSlug,
		uploadImage,
		addBlock,
		removeBlock,
		addCondition,
		removeCondition,
		updateCondition,
		handleMultiSelectChange,
		formatValuesForMultiSelect,
		checkItemCount,
		createSelectionPage,
		updateSelectionPage,
		handleChangeEntityType,
		handleRemoveEntityType
	} = useSelectionPage();

	const isTypeSKU = (type) => {
		return [
			"Product SKU",
			"Product ID",
			"Category",
			"Subcategory",
			"Supplier",
			"Brand",
			"Country",
			"Dietary",
		].includes(type);
	};

	const isTypeBoolean = (type) => {
		return ["Discount"].includes(type);
	};

	const handleAddSKU = (blockId, conditionIndex, field, {selectedList}) => {
		handleMultiSelectChange(blockId, conditionIndex, field, selectedList);
	};

	const handleRemoveSKU = (blockId, conditionIndex, field, {selectedList}) => {
		handleMultiSelectChange(blockId, conditionIndex, field, selectedList);
	};

	// Helper function to determine current page type
	const getPageType = () => {
		if (pageType === 'partner_page') return 'PARTNER';
		if (pageType === 'selection_page') return 'SELECTION';
		return formData.landing_page_type; // CATEGORY, SUBCATEGORY, RECIPE, MARKETING, SEASONAL
	};

	const currentPageType = getPageType();

	// Field visibility rules based on the requirements table
	const shouldShowShortName = currentPageType === 'RECIPE'; // Only Recipe
	const shouldShowSlug = true; // All pages show slug
	const shouldShowOrder = ['PARTNER', 'RECIPE'].includes(currentPageType); // Partner and Recipe only
	const shouldShowLogo = currentPageType === 'PARTNER'; // Partner only
	const shouldShowPoster = true; // Allow poster upload on all page types (partner, selection, category, subcategory, recipe, marketing, seasonal)

	console.log("formData", formData);
	return (
		<Box className={classes.root}>
			<Card className={classes.mainCard}>
				{/* Header */}
				<Box className={classes.header}>
					<Typography className={classes.headerTitle}>
						Products Collection Setup
					</Typography>
				</Box>

				{/* Content */}
				<Box className={classes.contentSection}>
					{/* Configuration Section - Landing Page Type & Entity Selection */}
					{pageType === 'landing_page' && (
						<>
							<Card elevation={0} style={{
								padding: "24px",
								marginBottom: "24px",
								background: "white",
								border: "1px solid #e2e8f0",
								borderRadius: "8px"
							}}>
								<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
									Page Configuration
								</Typography>
								<Grid container spacing={3}>
									{/* Landing Page Type */}
									<Grid item xs={12}
									      md={formData.landing_page_type === 'CATEGORY' || formData.landing_page_type === 'SUBCATEGORY' ? 6 : 12}>
										<Typography variant="subtitle2" style={{marginBottom: "8px", fontWeight: 600, color: "#4a5568"}}>
											Landing Page Type
										</Typography>
										<Select
											fullWidth
											variant="outlined"
											className={classes.selectField}
											value={formData.landing_page_type}
											onChange={(e) => updateFormField("landing_page_type", e.target.value)}
										>
											{landingPagesType.map((field, idx) => (
												<MenuItem key={idx} value={field.key}>
													{field.value}
												</MenuItem>
											))}
										</Select>
									</Grid>

									{/* Entity Selection - Show next to Landing Page Type when applicable */}
									{(formData.landing_page_type === 'CATEGORY' || formData.landing_page_type === 'SUBCATEGORY') && (
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" style={{marginBottom: "8px", fontWeight: 600, color: "#4a5568"}}>
												Entity Selection
											</Typography>
											<ItemSelected
												fieldType={formData.landing_page_type}
												value={formData.entityIds}
												onchange={handleChangeEntityType}
												onRemove={handleRemoveEntityType}
												placeholder={`Search ${formData.landing_page_type === 'CATEGORY' ? 'Category' : 'Subcategory'}`}
												blockType="entityType"
												singleSelect={true}
											/>
										</Grid>
									)}
								</Grid>
							</Card>
						</>
					)}

					{/* Basic Information */}
					<Card elevation={0} style={{
						padding: "24px",
						marginBottom: "24px",
						background: "white",
						border: "1px solid #e2e8f0",
						borderRadius: "8px"
					}}>
						<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
							Basic Information
						</Typography>
						<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<TextField
								label="Title"
								fullWidth
								value={formData.title}
								onChange={(e) => updateFormField("title", e.target.value)}
								variant="outlined"
								className={classes.inputField}
							/>
						</Grid>
						{shouldShowShortName && (
							<Grid item xs={12} md={6}>
								<TextField
									label="Short Name"
									fullWidth
									value={formData.short_name}
									onChange={(e) => updateFormField("short_name", e.target.value)}
									variant="outlined"
									className={classes.inputField}
								/>
							</Grid>
						)}
						{shouldShowSlug && (
							<Grid item xs={12} md={6}>
								<TextField
									label="Slug"
									fullWidth
									value={formData.slug}
									onChange={(e) => updateFormField("slug", e.target.value)}
									variant="outlined"
									className={classes.inputField}
									InputProps={{
										endAdornment: (
											<Tooltip title="Generate slug from title">
												<IconButton
													onClick={generateSlug}
													className={classes.generateSlugButton}
													size="small"
												>
													<BrushIcon/>
												</IconButton>
											</Tooltip>
										),
									}}
								/>
							</Grid>
						)}
						{shouldShowOrder && (
							<Grid item xs={12} md={6}>
								<TextField
									type="number"
									label="Order"
									variant="outlined"
									fullWidth
									value={formData.order}
									onChange={(e) => updateFormField("order", e.target.value)}
									className={classes.inputField}
								/>
							</Grid>
						)}
					</Grid>
					</Card>

					{/* Media Assets */}
					<Card elevation={0} style={{
						padding: "24px",
						marginBottom: "24px",
						background: "white",
						border: "1px solid #e2e8f0",
						borderRadius: "8px"
					}}>
						<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
							Media Assets
						</Typography>
						<Grid container spacing={3}>
						{/* Logo */}
						{shouldShowLogo && (
							<Grid item xs={12} md={6}>
								<Typography variant="subtitle2" style={{marginBottom: "12px", fontWeight: 600, color: "#4a5568"}}>
									Logo
								</Typography>
								<input
									type="file"
									id="logo-upload"
									style={{display: "none"}}
									onChange={(e) => uploadImage(e.target.files[0], "logo")}
								/>
								<label htmlFor="logo-upload">
									<Box className={classes.uploadButton}>
										<ImageIcon style={{fontSize: "48px", color: "#cbd5e0"}}/>
										<Typography variant="body2" style={{marginTop: "8px", color: "#718096"}}>
											Click to upload logo
										</Typography>
									</Box>
								</label>
								{!_.isEmpty(formData.logo) && (
									<Box display="flex" justifyContent="center" mt={2}>
										<Paper className={classes.logoPreview}>
											<img
												alt={formData.title}
												src={formData.logo}
												style={{width: "100%", height: "100%", objectFit: "cover"}}
											/>
										</Paper>
									</Box>
								)}
							</Grid>
						)}

						{/* Poster */}
						{shouldShowPoster && (
							<Grid item xs={12} md={6}>
								<Typography variant="subtitle2" style={{marginBottom: "12px", fontWeight: 600, color: "#4a5568"}}>
									Poster
								</Typography>
								<input
									type="file"
									id="poster-upload"
									style={{display: "none"}}
									onChange={(e) => uploadImage(e.target.files[0], "poster")}
								/>
								<label htmlFor="poster-upload">
									<Box className={classes.uploadButton}>
										<ImageIcon style={{fontSize: "48px", color: "#cbd5e0"}}/>
										<Typography variant="body2" style={{marginTop: "8px", color: "#718096"}}>
											Click to upload poster
										</Typography>
									</Box>
								</label>
								{!_.isEmpty(formData.poster) && (
									<Paper className={classes.imagePreview} style={{marginTop: "16px", padding: "8px"}}>
										<img
											alt={formData.title}
											src={formData.poster}
											style={{width: "100%", height: "200px", objectFit: "contain"}}
										/>
									</Paper>
								)}
							</Grid>
						)}

						{/* Banner */}
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" style={{marginBottom: "12px", fontWeight: 600, color: "#4a5568"}}>
								Banner
							</Typography>
							<input
								type="file"
								id="banner-upload"
								style={{display: "none"}}
								onChange={(e) => uploadImage(e.target.files[0], "banner")}
							/>
							<label htmlFor="banner-upload">
								<Box className={classes.uploadButton}>
									<ImageIcon style={{fontSize: "48px", color: "#cbd5e0"}}/>
									<Typography variant="body2" style={{marginTop: "8px", color: "#718096"}}>
										Click to upload banner
									</Typography>
								</Box>
							</label>
							{!_.isEmpty(formData.banner) && (
								<Paper className={classes.imagePreview} style={{marginTop: "16px", padding: "8px"}}>
									<img
										alt={formData.title}
										src={formData.banner}
										style={{width: "100%", height: "200px", objectFit: "contain"}}
									/>
								</Paper>
							)}
						</Grid>

						{/* Small Banner */}
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" style={{marginBottom: "12px", fontWeight: 600, color: "#4a5568"}}>
								Small Banner
							</Typography>
							<input
								type="file"
								id="small-banner-upload"
								style={{display: "none"}}
								onChange={(e) => uploadImage(e.target.files[0], "small_banner")}
							/>
							<label htmlFor="small-banner-upload">
								<Box className={classes.uploadButton}>
									<ImageIcon style={{fontSize: "48px", color: "#cbd5e0"}}/>
									<Typography variant="body2" style={{marginTop: "8px", color: "#718096"}}>
										Click to upload small banner
									</Typography>
								</Box>
							</label>
							{!_.isEmpty(formData.small_banner) && (
								<Paper className={classes.imagePreview} style={{marginTop: "16px", padding: "8px"}}>
									<img
										alt={formData.title}
										src={formData.small_banner}
										style={{width: "100%", height: "200px", objectFit: "contain"}}
									/>
								</Paper>
							)}
						</Grid>
					</Grid>
					</Card>

					{/* Description */}
					<Card elevation={0} style={{
						padding: "24px",
						marginBottom: "24px",
						background: "white",
						border: "1px solid #e2e8f0",
						borderRadius: "8px"
					}}>
						<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
							Description
						</Typography>
						<Box style={{background: "#f8faf9", padding: "16px", borderRadius: "8px"}}>
							<EditorTool
								value={formData.description}
								setValue={(value) => updateFormField("description", value)}
								page="products"
							/>
						</Box>
					</Card>

					{/* Settings Section */}
					<Card elevation={0} style={{
						padding: "24px",
						marginBottom: "24px",
						background: "white",
						border: "1px solid #e2e8f0",
						borderRadius: "8px"
					}}>
						<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
							Settings
						</Typography>
						<Grid container spacing={3}>
						{/* All Checkboxes */}
						<Grid item xs={12}>
							<Box display="flex" gap={4}>
								<FormControlLabel
									className={classes.checkboxLabel}
									control={
										<Checkbox
											checked={formData.status === 1}
											color={"primary"}
											onChange={(e) => updateFormField("status", e.target.checked ? 1 : 0)}
										/>
									}
									label="Active Status"
								/>
								<FormControlLabel
									className={classes.checkboxLabel}
									control={
										<Checkbox
											checked={formData.show_on_menu === 1}
											color={"primary"}
											onChange={(e) => updateFormField("show_on_menu", e.target.checked ? 1 : 0)}
										/>
									}
									label="Show on Partner Menu"
								/>
								<FormControlLabel
									className={classes.checkboxLabel}
									control={
										<Checkbox
											checked={formData.is_full_width === 1}
											color={"primary"}
											onChange={(e) => updateFormField("is_full_width", e.target.checked ? 1 : 0)}
										/>
									}
									label="Full Screen Mode"
								/>
							</Box>
						</Grid>
					</Grid>
					</Card>

					{/* Selection Criteria */}
					<Card elevation={0} style={{
						padding: "24px",
						marginBottom: "24px",
						background: "white",
						border: "1px solid #e2e8f0",
						borderRadius: "8px"
					}}>
						<Typography variant="h6" style={{marginBottom: "20px", fontWeight: 600, color: "#1a202c"}}>
							Selection Criteria
						</Typography>

						{loading ? (
						<Box display="flex" justifyContent="center" alignItems="center" py={8}>
							<CircularProgress style={{color: "#9cc54b"}} size={60}/>
						</Box>
					) : (
						<Fade in={!loading}>
							<Box>
								{blocks.map((block, blockIndex) => (
									<Box key={block.id}>
										<Card className={classes.conditionCard}>
											<Box className={classes.groupHeader}>
												<Box className={classes.groupBadge}>
													<LayersIcon/>
													Group {blockIndex + 1}
													<Box className={classes.productCount}>
														{checkItemCount(block.id)} products
													</Box>
												</Box>
												{blocks.length > 1 && (
													<Tooltip title="Remove this group">
														<IconButton
															onClick={() => removeBlock(block.id)}
															style={{color: "#e53e3e"}}
														>
															<CloseIcon/>
														</IconButton>
													</Tooltip>
												)}
											</Box>

											{block.conditions.map((condition, conditionIndex) => {
												const usedFields = block.conditions
													.filter((_, idx) => idx !== conditionIndex)
													.map((cond) => cond.field);

												return (
													<Box key={conditionIndex} className={classes.conditionRow}>
														<Grid container spacing={2} alignItems="center">
															<Grid item xs={12} md={3}>
																<Select
																	fullWidth
																	variant="outlined"
																	className={classes.selectField}
																	value={condition.field}
																	onChange={(e) =>
																		updateCondition(block.id, conditionIndex, "field", e.target.value)
																	}
																>
																	{fields.map((field, idx) => (
																		<MenuItem
																			key={idx}
																			disabled={usedFields.includes(field.field) && field.field !== condition.field}
																			value={field.field}
																		>
																			{field.field}
																		</MenuItem>
																	))}
																</Select>
															</Grid>

															<Grid item xs={12} md={2}>
																<Select
																	fullWidth
																	variant="outlined"
																	className={classes.selectField}
																	value={condition?.option}
																	onChange={(e) =>
																		updateCondition(block.id, conditionIndex, "option", e.target.value)
																	}
																>
																	{fields
																		.find((f) => f.field === condition.field)
																		?.options?.map((option, idx) => (
																			<MenuItem key={idx} value={option}>
																				{option}
																			</MenuItem>
																		))}
																</Select>
															</Grid>

															<Grid item xs={12} md={6}>
																{isTypeSKU(condition.field) ? (
																	<ItemSelected
																		fieldType={condition.field}
																		selecte={formatValuesForMultiSelect(condition.value, condition.field)}
																		onchange={handleAddSKU}
																		onRemove={handleRemoveSKU}
																		item={{block: block.id, conditionIndex}}
																		placeholder={`Search ${condition.field}`}
																	/>
																) : !isTypeBoolean(condition.field) ? (
																	<TextField
																		fullWidth
																		variant="outlined"
																		className={classes.inputField}
																		label={condition.field}
																		value={condition.value}
																		onChange={(e) =>
																			updateCondition(block.id, conditionIndex, "value", e.target.value)
																		}
																	/>
																) : null}
															</Grid>

															<Grid item xs={12} md={1}>
																{block.conditions.length > 1 && (
																	<Tooltip title="Remove condition">
																		<IconButton
																			onClick={() => removeCondition(block.id, conditionIndex, condition)}
																			style={{color: "#e53e3e"}}
																		>
																			<RemoveIcon/>
																		</IconButton>
																	</Tooltip>
																)}
															</Grid>
														</Grid>
													</Box>
												);
											})}

											<Button
												startIcon={<AddIcon/>}
												onClick={() => addCondition(block.id)}
												className={classes.addButton}
												style={{color: "#9cc54b", fontWeight: 600}}
											>
												Add Condition
											</Button>
										</Card>

										{blockIndex < blocks.length - 1 && (
											<Box className={classes.orDivider}>
												<Box className={classes.orText}>OR</Box>
											</Box>
										)}
									</Box>
								))}

								<Box display="flex" justifyContent="center" mt={3}>
									<Button
										startIcon={<AddIcon/>}
										onClick={addBlock}
										className={`${classes.actionButton} ${classes.primaryButton}`}
										variant="contained"
									>
										Add New Group
									</Button>
								</Box>
							</Box>
						</Fade>
					)}
					</Card>

					{/* Floating Action Bar */}
					<Box className={classes.floatingActionBar}>
						{type === "add" ? (
							<Button
								startIcon={<SaveIcon/>}
								className={`${classes.actionButton} ${classes.primaryButton}`}
								variant="contained"
								disabled={loading}
								onClick={createSelectionPage}
							>
								Create Page
							</Button>
						) : (
							<Button
								startIcon={<UpdateIcon/>}
								className={`${classes.actionButton} ${classes.primaryButton}`}
								variant="contained"
								disabled={loading}
								onClick={updateSelectionPage}
							>
								Update Page
							</Button>
						)}
					</Box>
				</Box>
			</Card>
		</Box>
	);
};

export default ComplexSelectionCriteria;